import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';

declare var $: any; // For jQuery
declare var Swiper: any; // For Swiper
declare var WOW: any; // For WOW.js

@Injectable({
  providedIn: 'root',
})
export class JqueryInitService {
  private isBrowser: boolean;
  private bannerSwiperInstance: any = null;
  private auctionSwiperInstance: any = null;
  private testimonialSwiperInstance: any = null;
  private categorySwiperInstance: any = null;
  private auctionDetailsSwiperInstance: any = null;
  private blogsSwiperInstance: any = null;
  private wowInstance: any = null;
  private scrollUpHandler: (() => void) | null = null;
  private passwordToggleHandlers = new Map<Element, EventListener>();

  constructor(private ngZone: NgZone, @Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Initialize all jQuery components and animations in a component
   * Call this from ngAfterViewInit in your component
   */
  initAll(dir: any) {
    if (!this.isBrowser) {
      console.log('Not in browser environment, skipping initialization');
      return;
    }
    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      // Check if jQuery is available
      if (typeof $ === 'undefined' || $ === null) {
        console.warn(
          'jQuery is not available. Skipping jQuery initializations.'
        );
        return;
      }

      try {
        this.cleanup();
        this.initButtonHoverEffects();
        this.initScrollUpButton();
        this.initSidebarEvents();
        this.initSearchEvents();
        this.initLanguageMenu();
        this.initSliders(dir);
        this.initAuctionDetailsNavSlider();
        // this.initCountdown();
        this.initGridViewSwitcher();
        this.initFilterSidebar();
        this.initCustomSelect();
        this.initQuantityControl();
        this.initPasswordToggle();
        this.initWowAnimations();
        this.initMarquee();
      } catch (e) {
        console.error('Error initializing jQuery components:', e);
      }
    });
  }

  /**
   * Initialize button hover effects
   */
  initButtonHoverEffects(selector: string = '.btn-hover') {
    if (!this.isBrowser) return;

    try {
      $(selector).off('mouseenter mouseleave'); // Remove any existing handlers
      $(selector).on('mouseenter', function (this: HTMLElement, e: MouseEvent) {
        const $this = $(this);
        const parentOffset = $this.offset();
        if (parentOffset) {
          const relX = e.pageX - parentOffset.left;
          const relY = e.pageY - parentOffset.top;
          $this.find('span').css({ top: 0, left: 0 });
          $this.find('span').css({ top: relY, left: relX });
        }
      });

      $(selector).on('mouseleave', function (this: HTMLElement, e: MouseEvent) {
        const $this = $(this);
        const parentOffset = $this.offset();
        if (parentOffset) {
          const relX = e.pageX - parentOffset.left;
          const relY = e.pageY - parentOffset.top;
          $this.find('span').css({ top: 0, left: 0 });
          $this.find('span').css({ top: relY, left: relX });
        }
      });
    } catch (e) {
      console.error('Error initializing button hover effects:', e);
    }
  }

  /**
   * Initialize sidebar events
   */
  initSidebarEvents() {
    if (!this.isBrowser) return;

    $('.sidebar-button')
      .off('click')
      .on('click', function (this: HTMLElement) {
        $(this).toggleClass('active');
        $('.main-menu').toggleClass('show-menu');
      });

    $('.menu-close-btn')
      .off('click')
      .on('click', function () {
        $('.main-menu').removeClass('show-menu');
      });

    // Sidebar
    $('.sidebar-btn')
      .off('click')
      .on('click', function () {
        $('.sidebar-area').addClass('active');
      });

    $('.sidebar-menu-close')
      .off('click')
      .on('click', function () {
        $('.sidebar-area').removeClass('active');
      });

    // Dropdown events
    $('.dropdown-icon')
      .off('click')
      .on('click', function (this: HTMLElement) {
        $(this).toggleClass('active').next('ul').slideToggle();
        $(this).parent().siblings().children('ul').slideUp();
        $(this).parent().siblings().children('.active').removeClass('active');
      });

    $('.dropdown-icon2')
      .off('click')
      .on('click', function (this: HTMLElement) {
        $(this).toggleClass('active').next('.submenu-list').slideToggle();
        $(this).parent().siblings().children('.submenu-list').slideUp();
        $(this).parent().siblings().children('.active').removeClass('active');
      });
  }

  /**
   * Initialize search events
   */
  initSearchEvents() {
    if (!this.isBrowser) return;

    $('.search-btn')
      .off('click')
      .on('click', function (this: HTMLElement, e: MouseEvent) {
        let parent = $(this).parent();
        parent.find('.search-input').toggleClass('active');
        e.stopPropagation();
      });

    $('.search-close')
      .off('click')
      .on('click', function () {
        $('.search-input').removeClass('active');
      });

    $(document)
      .off('click.searchEvent')
      .on('click.searchEvent', function (e: MouseEvent) {
        if (
          !$(e.target).closest(
            '.search-input, .search-btn, .sidebar-menu, .menu-icon'
          ).length
        ) {
          $('.search-input').removeClass('active');
          $('.sidebar-menu').removeClass('show-menu');
        }
      });
  }

  /**
   * Initialize language menu
   */
  initLanguageMenu() {
    if (!this.isBrowser) return;

    $('.language-btn')
      .off('click')
      .on('click', function (this: HTMLElement, e: MouseEvent) {
        let parent = $(this).parent();
        parent.find('.language-list').toggleClass('active');
        e.stopPropagation();
      });

    $(document)
      .off('click.languageEvent')
      .on('click.languageEvent', function (e: MouseEvent) {
        if (!$(e.target).closest('.language-btn').length) {
          $('.language-list').removeClass('active');
        }
      });
  }

  /**
   * Initialize all sliders using Swiper
   */
  initSliders(dir: any) {
    if (!this.isBrowser) return;

    // Only initialize if Swiper is available
    if (typeof Swiper === 'undefined') return;

    // Call individual slider initialization methods
    this.initTestimonialSlider();
    // this.initAuctionSlider();
    this.initCategorySlider();
    // this.initBannerSliders();
    // this.initBlogsSlider();
    // Add other slider initializations as needed
  }

  /**
   * Initialize testimonial slider
   */
  initTestimonialSlider() {
    if (!this.isBrowser) return;

    if ($('.testimonial-slider').length === 0) return;

    this.destroyTestimonialSlider();

    try {
      this.testimonialSwiperInstance = new Swiper('.testimonial-slider', {
        slidesPerView: 1,
        speed: 1500,
        spaceBetween: 25,
        autoplay: {
          delay: 2500,
          disableOnInteraction: false,
        },
        navigation: {
          nextEl: '.next-1',
          prevEl: '.prev-1',
        },
        breakpoints: {
          280: { slidesPerView: 1 },
          386: { slidesPerView: 1 },
          576: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          992: { slidesPerView: 2 },
          1200: { slidesPerView: 3 },
          1400: { slidesPerView: 3 },
        },
      });
    } catch (error) {
      console.error('Error initializing testimonial slider:', error);
    }
  }

  destroyTestimonialSlider() {
    if (!this.isBrowser || !this.testimonialSwiperInstance) return;

    try {
      this.testimonialSwiperInstance.destroy(true, true);
      this.testimonialSwiperInstance = null;
    } catch (error) {
      console.error('Error destroying testimonial slider:', error);
    }
  }

  /**
   * Initialize auction slider
   */
  initAuctionSlider(dir: any, cardAuctions: any) {
    if (!this.isBrowser) return;

    if ($('.auction-slider').length === 0) return;

    // Destroy existing instance first to prevent memory leaks
    this.destroyAuctionSlider();

    try {
      const shouldEnableNavigation = cardAuctions.length >= 5;

      this.auctionSwiperInstance = new Swiper('.auction-slider', {
        slidesPerView: 1,
        speed: 1500,
        spaceBetween: 50,
        autoplay: shouldEnableNavigation
          ? {
              delay: 2500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
        navigation: {
          nextEl: '.auction-slider-next',
          prevEl: '.auction-slider-prev',
        },
        breakpoints: {
          280: { slidesPerView: 1 },
          386: { slidesPerView: 1 },
          576: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          992: { slidesPerView: 3 },
          1200: { slidesPerView: 3 },
          1400: { slidesPerView: 3 },
          1600: { slidesPerView: 3 },
          1800: { slidesPerView: 3 },
        },
      });
      this.toggleNavigationButtons(shouldEnableNavigation);
    } catch (error) {
      console.error('Error initializing auction slider:', error);
    }
  }

  /**
   * Destroy auction slider instance
   */
  destroyAuctionSlider() {
    if (!this.isBrowser) return;

    if (this.auctionSwiperInstance) {
      try {
        this.auctionSwiperInstance.destroy(true, true);
        this.auctionSwiperInstance = null;
      } catch (error) {
        console.error('Error destroying auction slider:', error);
      }
    }
  }
  private toggleNavigationButtons(show: boolean) {
    const navigationButtons = $('.slider-btn-grp');

    if (show) {
      navigationButtons.removeClass('disabled');
    } else {
      navigationButtons.addClass('disabled');
    }
  }

  /**
   * Initialize auction Details slider
   */
  initAuctionDetailsNavSlider() {
    if (!this.isBrowser) return;

    if ($('.auction-details-nav-slider').length === 0) return;

    this.destroyAuctionDetailsNavSlider();

    try {
      this.auctionDetailsSwiperInstance = new Swiper(
        '.auction-details-nav-slider',
        {
        slidesPerView: 1,
        speed: 1500,
        spaceBetween: 15,
        autoplay: {
          delay: 2500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
        navigation: {
          nextEl: '.category-slider-next',
          prevEl: '.category-slider-prev',
        },
        breakpoints: {
          280: {
            slidesPerView: 2,
          },
          350: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
          576: {
            slidesPerView: 3,
            spaceBetween: 15,
          },
          768: {
            slidesPerView: 4,
          },
          992: {
            slidesPerView: 5,
            spaceBetween: 15,
          },
          1200: {
            slidesPerView: 5,
          },
          1400: {
            slidesPerView: 5,
          },
        },
        }
      );
    } catch (error) {
      console.error('Error initializing auction details slider:', error);
    }
  }

  destroyAuctionDetailsNavSlider() {
    if (!this.isBrowser || !this.auctionDetailsSwiperInstance) return;

    try {
      this.auctionDetailsSwiperInstance.destroy(true, true);
      this.auctionDetailsSwiperInstance = null;
    } catch (error) {
      console.error('Error destroying auction details slider:', error);
    }
  }
  /**
   * Initialize blogs slider
   */
  initBlogsSlider(dir: any) {
    if (!this.isBrowser) return;

    if ($('.home7-blog-slider').length === 0) return;

    this.destroyBlogsSlider();

    try {
      this.blogsSwiperInstance = new Swiper('.home7-blog-slider', {
        slidesPerView: 1,
        speed: 1500,
        spaceBetween: 25,
        // autoplay: {
        //   delay: 2500,
        //   disableOnInteraction: false,
        // },
        navigation: {
          nextEl: '.home7-blog-slider-next',
          prevEl: '.home7-blog-slider-prev',
        },
        breakpoints: {
          280: {
            slidesPerView: 1,
          },
          386: {
            slidesPerView: 1,
          },
          576: {
            slidesPerView: 1,
          },
          768: {
            slidesPerView: 2,
          },
          992: {
            slidesPerView: 3,
          },
          1200: {
            slidesPerView: 4,
          },
          1400: {
            slidesPerView: 4,
          },
        },
      });
    } catch (error) {
      console.error('Error initializing home7-blog-slider:', error);
    }
  }

  destroyBlogsSlider() {
    if (!this.isBrowser || !this.blogsSwiperInstance) return;

    try {
      this.blogsSwiperInstance.destroy(true, true);
      this.blogsSwiperInstance = null;
    } catch (error) {
      console.error('Error destroying blogs slider:', error);
    }
  }

  /**
   * Initialize category slider
   */
  initCategorySlider() {
    if (!this.isBrowser) return;

    if ($('.home1-category-slider').length === 0) return;

    this.destroyCategorySlider();

    try {
      this.categorySwiperInstance = new Swiper('.home1-category-slider', {
        slidesPerView: 1,
        speed: 1500,
        spaceBetween: 25,
        autoplay: {
          delay: 2500,
          disableOnInteraction: false,
        },
        navigation: {
          nextEl: '.category-slider-next',
          prevEl: '.category-slider-prev',
        },
        breakpoints: {
          280: { slidesPerView: 1 },
          375: { slidesPerView: 2, spaceBetween: 15 },
          576: { slidesPerView: 3, spaceBetween: 15 },
          768: { slidesPerView: 4 },
          992: { slidesPerView: 5, spaceBetween: 15 },
          1200: { slidesPerView: 6 },
          1400: { slidesPerView: 6 },
        },
      });
    } catch (error) {
      console.error('Error initializing category slider:', error);
    }
  }

  destroyCategorySlider() {
    if (!this.isBrowser || !this.categorySwiperInstance) return;

    try {
      this.categorySwiperInstance.destroy(true, true);
      this.categorySwiperInstance = null;
    } catch (error) {
      console.error('Error destroying category slider:', error);
    }
  }

  /**
   * Initialize banner sliders
   */
  initBannerSliders(dir?: string) {
    if (!this.isBrowser) return;

    // Destroy existing instance first to prevent conflicts
    this.destroyBannerSlider();

    // Home1 banner2 slider
    if ($('.home5-banner-slider').length > 0) {
      try {
        this.bannerSwiperInstance = new Swiper('.home5-banner-slider', {
          slidesPerView: 1,
          speed: 1500,
          spaceBetween: 25,
          centerSlides: true,
          loop: true,
          effect: 'fade',
          fadeEffect: {
            crossFade: true,
          },
          autoplay: {
            delay: 3000, // Autoplay duration in milliseconds
            disableOnInteraction: false,
          },
          navigation: {
            nextEl: '.home5-banner-slider-next',
            prevEl: '.home5-banner-slider-prev',
          },
          pagination: {
            el: '.paginations1',
            clickable: true,
          },
          rtl: dir === 'rtl',
        });
      } catch (error) {
        console.error('Error initializing home5-banner-slider:', error);
      }
    }

    // Add other banner slider initializations as needed
  }

  /**
   * Destroy banner slider instance
   */
  destroyBannerSlider() {
    if (!this.isBrowser) return;

    if (this.bannerSwiperInstance) {
      try {
        this.bannerSwiperInstance.destroy(true, true);
        this.bannerSwiperInstance = null;
      } catch (error) {
        console.error('Error destroying banner slider:', error);
      }
    }
  }

  /**
   * Initialize countdown timers
   */
  initCountdown() {
    if (!this.isBrowser) return;

    $('[data-countdown]').each(function (this: HTMLElement) {
      const $this = $(this);
      const deadlineStr = $this.data('countdown');
      if (!deadlineStr) return;

      const $deadline = new Date(deadlineStr).getTime();
      const $dataDays = $this.children('[data-days]');
      const $dataHours = $this.children('[data-hours]');
      const $dataMinutes = $this.children('[data-minutes]');
      const $dataSeconds = $this.children('[data-seconds]');

      // Clear any existing interval
      const existingIntervalId = $this.data('intervalId');
      if (existingIntervalId) {
        clearInterval(existingIntervalId);
      }

      const x = setInterval(function () {
        const now = new Date().getTime();
        const t = $deadline - now;
        const days = Math.floor(t / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (t % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((t % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((t % (1000 * 60)) / 1000);

        $dataDays.html(`${days} <span>Days</span> <span>Days</span>`);
        $dataHours.html(`${hours} <span>Hours</span> <span>Hours</span>`);
        $dataMinutes.html(`${minutes} <span>Mint</span> <span>Minutes</span>`);
        $dataSeconds.html(`${seconds} <span>Sec</span> <span>Seconds</span>`);

        if (t <= 0) {
          clearInterval(x);
          $dataDays.html('00 <span>Days</span> <span>Days</span>');
          $dataHours.html('00 <span>Hours</span> <span>Hours</span>');
          $dataMinutes.html('00 <span>Mint</span> <span>Minutes</span>');
          $dataSeconds.html('00 <span>Sec</span> <span>Seconds</span>');
        }
      }, 1000);

      // Store the interval ID on the element
      $this.data('intervalId', x);
    });
  }

  /**
   * Initialize grid view switcher
   */
  initGridViewSwitcher() {
    if (!this.isBrowser) return;

    $('.grid-view li')
      .off('click')
      .on('click', function (this: HTMLElement) {
        // Get the class of the clicked li element
        var clickedClass = $(this).attr('class');
        if (!clickedClass) return;

        // Extract the class name without "item-" prefix
        var className = clickedClass.replace('item-', '');
        // Add a new class to the target div and remove old classes
        var targetDiv = $('.list-grid-product-wrap');
        targetDiv
          .removeClass()
          .addClass('list-grid-product-wrap ' + className + '-wrapper');
        // Remove the 'selected' class from siblings and add it to the clicked element
        $(this).siblings().removeClass('active');
        $(this).addClass('active');
      });
  }

  /**
   * Initialize filter sidebar
   */
  initFilterSidebar() {
    if (!this.isBrowser) return;

    $('.filter')
      .off('click')
      .on('click', function (this: HTMLElement, e: MouseEvent) {
        e.stopPropagation();
        $('.filter-sidebar, .filter-top').toggleClass('slide');
      });

    $(document)
      .off('click.filterEvent')
      .on('click.filterEvent', function (e: MouseEvent) {
        if (
          !$(e.target).closest('.filter-sidebar, .filter-top, .filter').length
        ) {
          $('.filter-sidebar, .filter-top').removeClass('slide');
        }
      });
  }

  /**
   * Initialize custom select
   */
  initCustomSelect() {
    if (!this.isBrowser) return;

    // Handle click on the input item
    $('.select-input')
      .off('click')
      .on('click', function () {
        $('.custom-select-wrap').toggleClass('active');
      });

    $('.searchbox-input').each(function (this: HTMLElement) {
      const $container = $(this);

      $container
        .find('.option-list li')
        .off('click')
        .on('click', function (this: HTMLElement) {
          const destinationText = $(this).find('.destination h6, h6').text();
          $container.find('.select-input input').val(destinationText);
          $container.find('.custom-select-wrap').removeClass('active');
        });

      $container
        .find('.custom-select-search-area input')
        .off('input')
        .on('input', function (this: HTMLElement) {
          const searchText = $(this).val()?.toString().toLowerCase() || '';
          $container.find('.option-list li').each(function (this: HTMLElement) {
            const destinationText = $(this)
              .find('.destination h6')
              .text()
              .toLowerCase();
            if (destinationText.includes(searchText)) {
              $(this).show();
            } else {
              $(this).hide();
            }
          });
        });
    });

    $(document)
      .off('click.selectEvent')
      .on('click.selectEvent', function (e: MouseEvent) {
        if (!$(e.target).closest('.searchbox-input').length) {
          $('.custom-select-wrap').removeClass('active');
        }
      });
  }

  /**
   * Initialize quantity control
   */
  initQuantityControl() {
    if (!this.isBrowser) return;

    $('.quantity__minus')
      .off('click')
      .on('click', function (this: HTMLElement, e: MouseEvent) {
        e.preventDefault();
        const input = $(this).siblings('.quantity__input');
        const inputVal = input.val();
        if (typeof inputVal !== 'string') return;

        let value = parseInt(inputVal);
        if (value > 1) {
          value--;
        }
        input.val(value.toString().padStart(2, '0'));
      });

    $('.quantity__plus')
      .off('click')
      .on('click', function (this: HTMLElement, e: MouseEvent) {
        e.preventDefault();
        const input = $(this).siblings('.quantity__input');
        const inputVal = input.val();
        if (typeof inputVal !== 'string') return;

        let value = parseInt(inputVal);
        value++;
        input.val(value.toString().padStart(2, '0'));
      });
  }

  /**
   * Initialize password toggle for login/registration forms
   */
  initPasswordToggle() {
    if (!this.isBrowser) return;

    this.clearPasswordToggleHandlers();

    // For each toggle element, add the click handler
    const toggleElements = [
      '#togglePassword',
      '#togglePassword2',
      '#togglePassword3',
    ];
    const passwordElements = ['#password', '#password2', '#password3'];

    for (let i = 0; i < toggleElements.length; i++) {
      const toggleEl = document.querySelector(toggleElements[i]);
      const passwordEl = document.querySelector(
        passwordElements[i]
      ) as HTMLInputElement | null;

      if (toggleEl && passwordEl) {
        const clickHandler: EventListener = () => {
          // Toggle the type attribute
          const type =
            passwordEl.getAttribute('type') === 'password'
              ? 'text'
              : 'password';
          passwordEl.setAttribute('type', type);
          // Toggle the eye / eye slash icon
          toggleEl.classList.toggle('bi-eye');
        };

        toggleEl.addEventListener('click', clickHandler);
        this.passwordToggleHandlers.set(toggleEl, clickHandler);
      }
    }
  }

  private clearPasswordToggleHandlers() {
    this.passwordToggleHandlers.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.passwordToggleHandlers.clear();
  }

  /**
   * Initialize WOW.js animations
   */
  initWowAnimations() {
    if (!this.isBrowser) return;

    if (typeof WOW !== 'undefined') {
      if (this.wowInstance && typeof this.wowInstance.stop === 'function') {
        this.wowInstance.stop();
      }

      this.wowInstance = new WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 80,
        mobile: true,
        live: false, // Disabled continuous DOM scanning for performance
      });

      this.wowInstance.init();
    }
  }

  /**
   * Initialize marquee animations
   */
  initMarquee() {
    if (!this.isBrowser) return;

    if ($('.marquee_text').length > 0) {
      try {
        $('.marquee_text').marquee('destroy');
        $('.marquee_text').marquee({
          direction: 'left',
          duration: 25000,
          gap: 50,
          delayBeforeStart: 0,
          duplicated: true,
          startVisible: true,
        });
      } catch (error) {
        console.error('Error initializing marquee:', error);
      }
    }
  }

  /**
   * Initialize scrollup button
   */
  initScrollUpButton() {
    if (!this.isBrowser) return;

    let offset = 50;
    let circleContainer = document.querySelector(
      '.circle-container'
    ) as SVGPathElement | null;
    let circlePath = document.querySelector(
      '.circle-container path'
    ) as SVGPathElement | null;

    if (!circleContainer || !circlePath) return;

    let pathLength = circlePath.getTotalLength();

    // Fixed: correct casing for webkitTransition
    circlePath.style.transition = 'none';
    (circlePath.style as any).webkitTransition = 'none';

    circlePath.style.strokeDasharray = pathLength.toString();
    circlePath.style.strokeDashoffset = pathLength.toString();
    circlePath.getBoundingClientRect();

    // Fixed: correct casing for webkitTransition
    circlePath.style.transition = 'stroke-dashoffset 10ms linear';
    (circlePath.style as any).webkitTransition =
      'stroke-dashoffset 10ms linear';

    const updateLoader = () => {
      let scrollTop = window.scrollY;
      let docHeight = document.body.offsetHeight;
      let winHeight = window.innerHeight;
      let height = docHeight - winHeight;
      let progress = pathLength - (scrollTop * pathLength) / height;

      circlePath.style.strokeDashoffset = progress.toString();

      if (scrollTop > offset && circleContainer) {
        circleContainer.classList.add('active');
      } else {
        circleContainer.classList.remove('active');
      }
    };

    if (circleContainer) {
      circleContainer.onclick = function () {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      };
    }

    if (this.scrollUpHandler) {
      window.removeEventListener('scroll', this.scrollUpHandler);
    }

    this.scrollUpHandler = updateLoader;
    window.addEventListener('scroll', this.scrollUpHandler, { passive: true });

    // Initial call to set the correct state
    this.scrollUpHandler();
  }

  /**
   * Initialize Fancybox for galleries and videos
   */
  initFancybox() {
    if (!this.isBrowser) return;

    try {
      if ($('[data-fancybox="gallery"]').length > 0) {
        $('[data-fancybox="gallery"]').fancybox({
          buttons: ['close'],
          loop: false,
          protect: true,
        });
      }

      if ($('.video-player').length > 0) {
        $('.video-player').fancybox({
          buttons: ['close'],
          loop: false,
          protect: true,
        });
      }
    } catch (error) {
      console.error('Error initializing Fancybox:', error);
    }
  }

  /**
   * Initialize NiceSelect dropdown replacement
   */
  initNiceSelect() {
    if (!this.isBrowser) return;

    try {
      if ($('select').length > 0) {
        $('select').niceSelect();
      }
    } catch (error) {
      console.error('Error initializing NiceSelect:', error);
    }
  }

  /**
   * Initialize counter animation
   */
  initCounter() {
    if (!this.isBrowser) return;
    try {
      if ($('.counter').length > 0) {
        $('.counter').counterUp({
          delay: 10,
          time: 1500,
        });
      }
    } catch (error) {
      console.error('Error initializing Counter:', error);
    }
  }

  /**
   * Cleanup all event listeners and intervals when component is destroyed
   * Call this from ngOnDestroy in your component
   */
  cleanup() {
    if (!this.isBrowser) {
      return;
    }

    this.clearPasswordToggleHandlers();

    if (this.scrollUpHandler) {
      window.removeEventListener('scroll', this.scrollUpHandler);
      this.scrollUpHandler = null;
    }

    if (this.wowInstance && typeof this.wowInstance.stop === 'function') {
      this.wowInstance.stop();
      this.wowInstance = null;
    }

    this.destroyAuctionSlider();
    this.destroyBannerSlider();
    this.destroyTestimonialSlider();
    this.destroyCategorySlider();
    this.destroyAuctionDetailsNavSlider();
    this.destroyBlogsSlider();

    try {
      // Check if jQuery is available before using it
      if (typeof $ !== 'undefined' && $ !== null) {
        try {
          // Remove all event listeners
          $(document).off('click.searchEvent');
          $(document).off('click.languageEvent');
          $(document).off('click.filterEvent');
          $(document).off('click.selectEvent');
        } catch (e) {
          console.warn('Error removing jQuery document event listeners:', e);
        }

        try {
          // Stop all timers for countdown
          $('[data-countdown]').each(function (this: HTMLElement) {
            try {
              const intervalId = $(this).data('intervalId');
              if (intervalId) {
                clearInterval(intervalId);
              }
            } catch (e) {
              console.warn('Error clearing interval:', e);
            }
          });
        } catch (e) {
          console.warn('Error processing countdown elements:', e);
        }

        try {
          $('.marquee_text').marquee('destroy');
        } catch (e) {
          console.warn('Error destroying marquee:', e);
        }
      }
    } catch (e) {
      console.warn('Error in cleanup method:', e);
    }
  }
}
